import { useRef, useEffect, useCallback, useMemo, useState } from 'react';

import * as Sentry from '@sentry/browser';
import { useIntl } from 'react-intl';
import { useResource, useRouterActions } from 'react-resource-router';

import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import Form, { Field } from '@atlaskit/form';
import Modal, {
  ModalFooter,
  ModalTransition,
  ModalTitle,
  ModalHeader,
  ModalBody,
} from '@atlaskit/modal-dialog';
import { Flex } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';

import { MergeQueue } from 'src/components/pipelines/models/MergeQueue';
import queryBranches from 'src/components/pipelines/utils/query-branches';
import { useAnalytics } from 'src/components/settings/analytics';
import { StatsigFeatureKeys } from 'src/features';
import { useStatsigGate } from 'src/hooks/statsig';
import { useParams } from 'src/router/hooks';
import {
  TextField,
  SelectField,
  AsyncSelectField,
} from 'src/sections/global/components/form-components';
import { getStrategyOption } from 'src/sections/repository/utils/merge-strategies';
import { MergeStrategy } from 'src/types/pull-request';

import { useCreateEditMergeQueue } from '../hooks/use-create-edit-merge-queue';
import { messages } from '../merge-queues.i18n';
import {
  useFocusedMergeQueue,
  useIsCreateModalOpen,
  useIsEditModalOpen,
  useMergeQueues,
  useMergeQueuesActions,
} from '../provider';
import { mergeQueuesRouteResource } from '../resources';
import {
  BranchesResponse,
  MergeQueueFormState,
  MergeQueueMergeStrategy,
  MergeQueueResponse,
  MergeQueueStrategy,
  PullRequestsOrderingCriteria,
  WaitTimeUnit,
} from '../types';
import { mergeQueueToFormValues, preventNonNumericKeyPress } from '../utils';

const WARNING_SECTION_FIELD_NAME = 'WARNING_SECTION_FIELD';

const WarningMessage = ({ submitError }: { submitError: string }) =>
  submitError ? (
    <SectionMessage appearance="error">{submitError}</SectionMessage>
  ) : null;

const MergeQueueModalFooter = ({
  onClose,
  isModalInEditMode,
  isSubmitting,
  formatMessage,
  publishButtonClickEvent,
}: {
  onClose: () => void;
  isModalInEditMode: boolean;
  isSubmitting: boolean;
  formatMessage: (message: any) => string;
  publishButtonClickEvent: (action: string) => void;
}) => (
  <ModalFooter>
    <ButtonGroup>
      <Button
        appearance="subtle"
        onClick={() => {
          publishButtonClickEvent('cancel');
          onClose();
        }}
        isDisabled={isSubmitting}
      >
        {formatMessage(messages.cancel)}
      </Button>
      <Button
        appearance="primary"
        type="submit"
        isLoading={isSubmitting}
        autoFocus
        onClick={() =>
          isModalInEditMode
            ? publishButtonClickEvent('saveMergeQueue')
            : publishButtonClickEvent('createMergeQueue')
        }
      >
        {isModalInEditMode
          ? formatMessage(messages.save)
          : formatMessage(messages.create)}
      </Button>
    </ButtonGroup>
  </ModalFooter>
);

export const MergeQueueCreateEditModalContent = () => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const { publishScreenEvent, publishButtonClickEvent } = useAnalytics({
    source: 'MergeQueueCreateEditModal',
  });

  const [{ focusedMergeQueue }] = useFocusedMergeQueue();
  const [{ isCreateModalOpen }] = useIsCreateModalOpen();
  const [{ isEditModalOpen }] = useIsEditModalOpen();
  const [{ mergeQueues }, { setIsCreateModalOpen, setIsEditModalOpen }] =
    useMergeQueues();
  const [, { setFocusedMergeQueue }] = useMergeQueuesActions();
  const { push } = useRouterActions();

  const isModalInEditMode = useMemo(
    () =>
      isEditModalOpen &&
      !isCreateModalOpen &&
      focusedMergeQueue?.uuid !== undefined,
    [isCreateModalOpen, isEditModalOpen, focusedMergeQueue]
  );

  const { mergeQueueNames, mergeQueueBranches } = useMemo(() => {
    return mergeQueues.reduce(
      (acc, mergeQueue) => {
        acc.mergeQueueNames.add(mergeQueue.name);
        acc.mergeQueueBranches.add(mergeQueue.branch.name);
        return acc;
      },
      { mergeQueueNames: new Set(), mergeQueueBranches: new Set() }
    );
  }, [mergeQueues]);

  const { refresh: refreshMergeQueues } = useResource(mergeQueuesRouteResource);

  const isBranchPrefixInMergeQueuesEnabled = useStatsigGate(
    StatsigFeatureKeys.isBranchPrefixInMergeQueuesEnabled
  );

  const isMergeQueuesLandingAreaEnabled = useStatsigGate(
    StatsigFeatureKeys.isMergeQueuesLandingAreaEnabled
  );

  const isMergeQueuesStrategyEnabled = useStatsigGate(
    StatsigFeatureKeys.isMergeQueuesStrategyEnabled
  );

  useEffect(() => {
    publishScreenEvent();
  }, [publishScreenEvent]);

  const { repositoryOwner, repositorySlug } = useParams<{
    repositoryOwner: string;
    repositorySlug: string;
  }>();

  if (!repositoryOwner || !repositorySlug) {
    throw new Error(
      'This component must be rendered on a route that includes both a :repositoryOwner and a :repositorySlug'
    );
  }

  const strategyOptions = useMemo(
    () => [
      {
        value: MergeQueueStrategy.OPTIMISTIC_BATCHED_PR,
        label: formatMessage(messages.queueStrategyOptimisticBatchedPR),
      },
      {
        value: MergeQueueStrategy.OPTIMISTIC_PER_PR,
        label: formatMessage(messages.queueStrategyOptimisticPerPR),
      },
      {
        value: MergeQueueStrategy.STRICT_PER_PR,
        label: formatMessage(messages.queueStrategyStrictPerPR),
      },
    ],
    [formatMessage]
  );

  const waitTimeUnitOptions = useMemo(
    () => [
      {
        label: formatMessage(messages.waitTimeUnitSeconds),
        value: WaitTimeUnit.SECONDS,
      },
      {
        label: formatMessage(messages.waitTimeUnitMinutes),
        value: WaitTimeUnit.MINUTES,
      },
      {
        label: formatMessage(messages.waitTimeUnitHours),
        value: WaitTimeUnit.HOURS,
      },
    ],
    [formatMessage]
  );

  const mergeStrategyOptions = useMemo(
    () =>
      [
        getStrategyOption(MergeStrategy.MergeCommit, intl),
        getStrategyOption(MergeStrategy.Squash, intl),
        getStrategyOption(MergeStrategy.RebaseMerge, intl),
      ] as Array<{
        label: string;
        value: MergeQueueMergeStrategy;
        description?: string;
      }>,
    [intl]
  );

  const pullRequestsOrderingCriteriaOptions = useMemo(
    () => [
      {
        label: formatMessage(messages.pullRequestsOrderingCriteriaTimeBased),
        value: PullRequestsOrderingCriteria.TIME_BASED,
      },
      {
        label: formatMessage(
          messages.pullRequestsOrderingCriteriaBranchPatternAndTimeBased
        ),
        value: PullRequestsOrderingCriteria.BRANCH_PATTERN_AND_TIME_BASED,
      },
    ],
    [formatMessage]
  );

  const afmDisableBranchPrefetching = useStatsigGate(
    StatsigFeatureKeys.afmDisableBranchPrefetching
  );

  const loadBranches = useCallback(
    async (query: string = '') => {
      // Only fetch branches if repository info is available and (feature flag is off OR user has typed a search term)
      if (
        !repositoryOwner ||
        !repositorySlug ||
        (afmDisableBranchPrefetching && !query)
      ) {
        return [];
      }
      try {
        const response: BranchesResponse = await queryBranches(
          `${repositoryOwner}/${repositorySlug}`,
          query
        );
        return response.values.map(({ name }) => ({
          label: name,
          value: name,
          isDisabled: mergeQueueBranches.has(name),
        }));
      } catch (error) {
        Sentry.captureException(error);
        return [];
      }
    },
    [
      repositoryOwner,
      repositorySlug,
      mergeQueueBranches,
      afmDisableBranchPrefetching,
    ]
  );

  const formValuesRef = useRef<Partial<MergeQueueFormState>>(
    isModalInEditMode && focusedMergeQueue
      ? mergeQueueToFormValues(focusedMergeQueue)
      : {}
  );

  const [showSourceBranchPatterns, setShowSourceBranchPatterns] = useState(
    formValuesRef?.current?.pullRequestsOrdering?.criteria ===
      PullRequestsOrderingCriteria.BRANCH_PATTERN_AND_TIME_BASED
  );

  const onClose = useCallback(() => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
  }, [setIsCreateModalOpen, setIsEditModalOpen]);

  const onCreateOrEditSuccess = useCallback(
    (createdMergeQueue?: MergeQueueResponse) => {
      if (createdMergeQueue) {
        setFocusedMergeQueue(new MergeQueue(createdMergeQueue));
        push(
          `/${repositoryOwner}/${repositorySlug}/admin/merge-queues/configs/${createdMergeQueue.uuid}`
        );
      }
      refreshMergeQueues();
    },
    [
      repositoryOwner,
      repositorySlug,
      push,
      refreshMergeQueues,
      setFocusedMergeQueue,
    ]
  );

  const {
    createMergeQueue,
    isCreatingMergeQueue,
    editMergeQueue,
    isEditingMergeQueue,
  } = useCreateEditMergeQueue({
    mergeQueueUuid: isModalInEditMode ? focusedMergeQueue?.uuid : undefined,
    owner: repositoryOwner,
    slug: repositorySlug,
    onClose,
    onSuccess: onCreateOrEditSuccess,
  });

  const validateMergeQueueName = useCallback(
    (value: string) => {
      if (isModalInEditMode) {
        return undefined;
      }

      // Check if name already exists
      if (mergeQueueNames.has(value)) {
        return formatMessage(messages.mergeQueueNameAlreadyExists);
      }

      // Check if name starts with an alphabet
      if (!/^[a-zA-Z]/.test(value)) {
        return formatMessage(messages.mergeQueueNameMustStartWithAlphabet);
      }

      // Check if name contains only alphanumeric characters, hyphens, and underscores
      if (!/^[a-zA-Z0-9_-]+$/.test(value)) {
        return formatMessage(messages.mergeQueueNameInvalidCharacters);
      }
      return undefined;
    },
    [formatMessage, isModalInEditMode, mergeQueueNames]
  );

  const validateSourceBranchPatterns = useCallback(
    (value: string) => {
      if (!value?.trim()) {
        return formatMessage(messages.sourceBranchPatternsRequired);
      }

      // Valid Ant patterns can contain: alphanumeric, *, **, ?, /, -, _, .
      const antPatternRegex = /^[a-zA-Z0-9*?/_.-]+$/;
      // Patterns consisting only of wildcards (*, ?, or combinations) are too generic
      const wildcardOnlyRegex = /^[*?]+$/;

      const patterns = value
        .split(',')
        .map(p => p.trim())
        .filter(Boolean);

      if (patterns.length === 0) {
        return formatMessage(messages.sourceBranchPatternsRequired);
      }

      for (const pattern of patterns) {
        if (!antPatternRegex.test(pattern)) {
          return formatMessage(messages.sourceBranchPatternsInvalidAntPattern);
        }

        if (wildcardOnlyRegex.test(pattern)) {
          return formatMessage(messages.sourceBranchPatternsTooGeneric);
        }
      }

      return undefined;
    },
    [formatMessage]
  );

  const onSubmit = useCallback(
    (data: MergeQueueFormState) => {
      if (isModalInEditMode) {
        const formValues: MergeQueueFormState = {
          ...formValuesRef.current,
          ...data,
        };
        editMergeQueue(formValues);
      } else {
        createMergeQueue(
          isMergeQueuesStrategyEnabled
            ? data
            : { ...data, strategy: MergeQueueStrategy.STRICT_PER_PR }
        );
      }
    },
    [
      createMergeQueue,
      editMergeQueue,
      isModalInEditMode,
      isMergeQueuesStrategyEnabled,
    ]
  );

  return (
    <Modal
      testId="merge-queue-config-modal"
      width={656}
      onClose={onClose}
      shouldScrollInViewport
    >
      <ModalHeader hasCloseButton>
        <ModalTitle>
          {isModalInEditMode
            ? formatMessage(messages.modalEditHeader)
            : formatMessage(messages.modalHeader)}
        </ModalTitle>
      </ModalHeader>
      <Form onSubmit={onSubmit}>
        {({ formProps, submitting }) => (
          <form {...formProps}>
            <ModalBody>
              <Field name={WARNING_SECTION_FIELD_NAME}>
                {({ meta: { submitError } }) => (
                  <WarningMessage
                    submitError={submitError ? String(submitError) : ''}
                  />
                )}
              </Field>
              <TextField
                defaultValue={formValuesRef.current?.name || ''}
                isDisabled={isModalInEditMode}
                isRequired
                label={formatMessage(messages.mergeQueueNameLabel)}
                name="name"
                placeholder={formatMessage(messages.mergeQueueNamePlaceholder)}
                testId="merge-queue-name"
                width={320}
                validate={validateMergeQueueName}
              />
              <AsyncSelectField
                isDisabled={isModalInEditMode}
                isRequired
                name="targetBranchName"
                defaultValue={
                  formValuesRef.current.targetBranchName
                    ? {
                        label: formValuesRef.current.targetBranchName,
                        value: formValuesRef.current.targetBranchName,
                      }
                    : undefined
                }
                label={formatMessage(messages.targetBranchTableHeader)}
                placeholder={formatMessage(messages.selectBranchPlaceholder)}
                loadOptions={loadBranches}
                cacheOptions
                defaultOptions
              />
              {isMergeQueuesStrategyEnabled && (
                <SelectField
                  isDisabled={isModalInEditMode}
                  isRequired
                  defaultFieldValue={
                    formValuesRef.current?.strategy ??
                    MergeQueueStrategy.STRICT_PER_PR
                  }
                  label={formatMessage(messages.queueStrategyLabel)}
                  name="strategy"
                  placeholder={formatMessage(messages.queueStrategyPlaceholder)}
                  options={strategyOptions}
                />
              )}
              <SelectField
                defaultFieldValue={formValuesRef.current?.mergeStrategy}
                label={formatMessage(messages.mergeStrategy)}
                name="mergeStrategy"
                placeholder={formatMessage(messages.mergeStrategyPlaceholder)}
                options={mergeStrategyOptions}
              />
              <TextField
                name="buildConcurrency"
                testId="build-concurrency"
                label={formatMessage(messages.buildConcurrency)}
                defaultValue={formValuesRef.current?.buildConcurrency || 1}
                type="number"
                min={1}
                max={100}
                onKeyPress={preventNonNumericKeyPress}
                width={320}
              />
              {isBranchPrefixInMergeQueuesEnabled && (
                <>
                  <SelectField
                    defaultFieldValue={
                      formValuesRef.current?.pullRequestsOrdering?.criteria
                    }
                    label={formatMessage(messages.pullRequestsOrderingCriteria)}
                    name="pullRequestsOrderingCriteria"
                    placeholder={formatMessage(
                      messages.pullRequestsOrderingCriteriaPlaceholder
                    )}
                    options={pullRequestsOrderingCriteriaOptions}
                    onChange={e => {
                      if (e && 'value' in e) {
                        setShowSourceBranchPatterns(
                          e.value ===
                            PullRequestsOrderingCriteria.BRANCH_PATTERN_AND_TIME_BASED
                        );
                      }
                    }}
                  />
                  {showSourceBranchPatterns && (
                    <TextField
                      isRequired
                      label={formatMessage(messages.sourceBranchPatternsLabel)}
                      name="sourceBranchPatterns"
                      testId="source-branch-patterns"
                      defaultValue={
                        formValuesRef.current?.pullRequestsOrdering?.sourceBranchPatterns?.join(
                          ', '
                        ) || ''
                      }
                      placeholder={formatMessage(
                        messages.sourceBranchPatternsPlaceholder
                      )}
                      validate={validateSourceBranchPatterns}
                      width={320}
                    />
                  )}
                </>
              )}
              {isMergeQueuesLandingAreaEnabled && (
                <>
                  <TextField
                    name="mergeBatchSize"
                    testId="merge-batch-size"
                    label={formatMessage(messages.mergeBatchSize)}
                    defaultValue={formValuesRef.current?.mergeBatchSize || 1}
                    width={320}
                    type="number"
                    min={1}
                    max={100}
                    onKeyPress={preventNonNumericKeyPress}
                  />
                  <Flex alignItems="end" gap="space.200">
                    <TextField
                      name="maximumBatchWaitTime"
                      testId="maximum-batch-wait-time"
                      label={formatMessage(messages.maximumBatchWaitTime)}
                      defaultValue={
                        formValuesRef.current?.maximumBatchWaitTime || 0
                      }
                      type="number"
                      min={0}
                      max={60}
                      onKeyPress={preventNonNumericKeyPress}
                    />
                    <SelectField
                      name="maximumBatchWaitTimeUnit"
                      label=""
                      defaultFieldValue={
                        formValuesRef.current?.maximumBatchWaitTimeUnit ??
                        WaitTimeUnit.MINUTES
                      }
                      options={waitTimeUnitOptions}
                    />
                  </Flex>
                </>
              )}
            </ModalBody>
            <MergeQueueModalFooter
              onClose={onClose}
              isModalInEditMode={isModalInEditMode}
              isSubmitting={
                submitting || isCreatingMergeQueue || isEditingMergeQueue
              }
              formatMessage={formatMessage}
              publishButtonClickEvent={publishButtonClickEvent}
            />
          </form>
        )}
      </Form>
    </Modal>
  );
};

export const MergeQueueCreateEditModal = () => {
  const [{ isCreateModalOpen }] = useIsCreateModalOpen();
  const [{ isEditModalOpen }] = useIsEditModalOpen();

  return (
    <ModalTransition>
      {(isCreateModalOpen || isEditModalOpen) && (
        <MergeQueueCreateEditModalContent />
      )}
    </ModalTransition>
  );
};
